import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAuth } from '@/hooks/use-auth';
import { useImport } from '@/hooks/use-import';
import { useTeamChat } from '@/hooks/use-team-chat';
import { endCollabSession } from '@/lib/api/team';
import { collabSession, useCollabSessionSnapshot } from '@/lib/team/collabSession';
import { cn } from '@/lib/utils';
import { MessagesSquare, Radio } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
// The panel pulls in the whole chat tree (conversations, threads, roster,
// composer): loaded only once the dock actually renders — i.e. for a user
// with a chat-enabled group — so everyone else never downloads it.
const TeamPanel = lazy(() => import('./TeamPanel').then((m) => ({ default: m.TeamPanel })));
/** The import card's tallest state (uploading: progress, bytes and the
 * keep-this-tab-open note, wrapped on a phone) plus a gap. */
const IMPORT_CARD_CLEARANCE = '12.5rem';
/**
 * Global "you are presenting" indicator. The viewer's presenter pill exists
 * only on the presented study, but presenter mode survives navigation by
 * design — without this, a presenter who hops to the worklist has no End
 * control at all while followers stay frozen on the last broadcast. Mirrors
 * useCollabPresenter.endSession: REST end, then local stop regardless.
 */
function PresentingPill() {
    const { t } = useTranslation();
    const session = useCollabSessionSnapshot();
    if (session.role !== 'presenter' || session.sessionId === null)
        return null;
    const sessionId = session.sessionId;
    const end = async () => {
        try {
            await endCollabSession(sessionId);
        }
        catch {
            // Already ended / offline — still stop presenting locally.
        }
        collabSession.stop();
    };
    return (_jsxs("div", { "data-testid": "team-chat-presenting-pill", className: "border-primary/40 bg-panel-chrome/95 flex h-10 items-center gap-2 rounded-full border py-1 pr-1.5 pl-3 shadow-lg shadow-black/30 backdrop-blur-xl", children: [_jsx(Radio, { className: "text-primary h-3.5 w-3.5 animate-pulse" }), _jsx("span", { className: "text-foreground text-xs font-medium", children: t('teamChat.collabActive') }), _jsx("button", { type: "button", "data-testid": "team-chat-end-collab", onClick: () => void end(), 
                // touch-hitbox (index.css) takes the tap target to 44px tall under a
                // coarse pointer without growing the button out of the h-10 pill.
                className: "bg-destructive/80 hover:bg-destructive text-destructive-foreground touch-hitbox relative rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors", children: t('teamChat.endCollab') })] }));
}
/**
 * The Team Chat dock: a floating pill (bottom-right, above everything but nav
 * and dialogs) plus the slide-up panel. Deliberately NOT in TopNav — the viewer
 * has no TopNav, and chat must be reachable while reading. Renders nothing at
 * all when the user has no chat-enabled group (the feature is invisible until
 * an admin flips the group flag) or on unauthenticated/public surfaces.
 *
 * The panel stays MOUNTED while closed (state survives open/close and route
 * changes); visibility is a scale/fade transition, macOS-sheet style. While
 * closed the container is `inert` so its buttons leave the tab order — an
 * aria-hidden subtree with live focus stops is a keyboard trap.
 */
export function TeamChatDock() {
    const { t } = useTranslation();
    const { isAuthenticated } = useAuth();
    const { enabled, panelOpen, togglePanel, totalUnread, onlineCount } = useTeamChat();
    const { state: importState } = useImport();
    if (!isAuthenticated || !enabled)
        return null;
    // ImportProgressIndicator's card (z-50) shares this corner and covered the
    // pill and the End control: while it is up, the dock sits above it. The
    // report pages' action bar (Preview / Finalize) holds the same corner below
    // xl: its height is published as --toast-bottom-offset (useToastClearance)
    // and the dock adds it on every pointer type. TeamPanel subtracts both from
    // its height.
    // The accessible name carries what the pill shows (unread, online), which a
    // bare "Team Chat" label would hide from screen-reader users.
    const toggleLabel = [
        t('teamChat.title'),
        totalUnread > 0 ? t('teamChat.unreadCount', { count: totalUnread }) : null,
        onlineCount > 0 ? t('teamChat.onlineCount', { count: onlineCount }) : null,
    ]
        .filter(Boolean)
        .join(', ');
    const lift = {
        '--team-dock-lift': importState.status === 'idle' ? '0rem' : IMPORT_CARD_CLEARANCE,
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: cn(
                // Clear of the iPhone notch and home indicator (index.html sets
                // viewport-fit=cover; env() is 0 elsewhere), as the import card does.
                'fixed right-[max(1rem,env(safe-area-inset-right))] bottom-[calc(max(1rem,env(safe-area-inset-bottom))_+_3rem_+_var(--team-dock-lift)_+_var(--toast-bottom-offset,0px))] z-30 origin-bottom-right transition-all duration-200 ease-out', panelOpen
                    ? 'translate-y-0 scale-100 opacity-100'
                    : 'pointer-events-none translate-y-2 scale-95 opacity-0'), style: lift, "aria-hidden": !panelOpen, inert: !panelOpen, "data-testid": "team-chat-panel-container", id: "team-chat-panel-container", children: _jsx(Suspense, { fallback: null, children: _jsx(TeamPanel, {}) }) }), _jsxs("div", { className: "fixed right-[max(1rem,env(safe-area-inset-right))] bottom-[calc(max(1rem,env(safe-area-inset-bottom))_+_var(--team-dock-lift)_+_var(--toast-bottom-offset,0px))] z-30 flex items-center gap-2", style: lift, "data-testid": "team-chat-dock", children: [_jsx(PresentingPill, {}), _jsxs("button", { type: "button", onClick: togglePanel, "data-testid": "team-chat-toggle", "aria-label": toggleLabel, "aria-expanded": panelOpen, "aria-controls": "team-chat-panel-container", className: cn('flex h-10 items-center gap-2 rounded-full px-3.5', 'bg-panel-chrome/80 border border-white/10 shadow-lg shadow-black/30 backdrop-blur-xl', 'text-foreground hover:bg-panel-chrome transition-all duration-150 hover:scale-[1.03] active:scale-95', panelOpen && 'ring-primary/40 ring-1'), children: [_jsx(MessagesSquare, { className: "text-primary h-4 w-4" }), _jsx("span", { className: "text-xs font-medium", children: t('teamChat.title') }), onlineCount > 0 && (_jsxs("span", { className: "flex items-center gap-1 text-[11px] text-emerald-400", children: [_jsx("span", { className: "inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px] shadow-emerald-500/60" }), onlineCount] })), totalUnread > 0 && (_jsx("span", { "data-testid": "team-chat-unread-badge", className: "bg-primary text-primary-foreground flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold shadow-sm", children: totalUnread > 99 ? '99+' : totalUnread }))] })] })] }));
}
